import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../../components/common';
import { FiSend, FiPaperclip, FiMoreVertical, FiArrowLeft } from 'react-icons/fi';
import AC from 'agora-chat';
import { fetchAgoraChatToken } from '../../api/expert';
import routes from '../../utils/routes';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'expert';
  timestamp: Date;
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
}

// Agora Chat App Key - should be fetched from backend or env
const AGORA_APP_KEY = process.env.REACT_APP_AGORA_CHAT_APP_KEY || 'YOUR_APP_KEY';

const ChatScreen: React.FC = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const expertName = searchParams.get('expert') || 'Expert';
  const expertAgoraId = searchParams.get('expertId') || '';

  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  // Agora Chat state
  const connRef = useRef<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isExpertOnline, setIsExpertOnline] = useState(false);

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [sessionTime, setSessionTime] = useState(30 * 60); // 30 minutes in seconds
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle incoming messages
  const handleMessage = useCallback((msg: any) => {
    const newMessage: Message = {
      id: msg.id || Date.now().toString(),
      text: msg.msg || msg.data || '',
      sender: msg.from === user?.agora_user_id ? 'user' : 'expert',
      timestamp: new Date(msg.time || Date.now()),
      status: 'delivered',
    };
    setMessages((prev) => [...prev, newMessage]);
  }, [user?.agora_user_id]);

  // Initialize Agora Chat connection
  useEffect(() => {
    const initAgoraChat = async () => {
      if (!bookingId) {
        setError('No booking ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch Agora chat token from backend
        const tokenData = await fetchAgoraChatToken();
        const { token, uid } = tokenData;

        // Create Agora Chat connection
        const conn = new AC.connection({
          appKey: AGORA_APP_KEY,
        });
        connRef.current = conn;

        // Set up event handlers
        conn.addEventHandler('chatHandler', {
          onConnected: () => {
            console.log('Agora Chat connected');
            setIsConnected(true);
            setLoading(false);
          },
          onDisconnected: () => {
            console.log('Agora Chat disconnected');
            setIsConnected(false);
          },
          onTextMessage: (msg: any) => {
            handleMessage(msg);
          },
          onError: (error: any) => {
            console.error('Agora Chat error:', error);
            setError('Chat connection error');
          },
          onPresenceStatusChange: (msg: any) => {
            // Check if expert came online/offline
            if (msg.userId === expertAgoraId) {
              setIsExpertOnline(msg.status === 'online');
            }
          },
        });

        // Open connection with token
        await conn.open({
          user: uid.toString(),
          agoraToken: token,
        });

        // Subscribe to expert's presence
        if (expertAgoraId) {
          try {
            await conn.subscribePresence({
              usernames: [expertAgoraId],
              expiry: 7200, // 2 hours
            });
          } catch (err) {
            console.log('Presence subscription not available');
          }
        }

        // Add welcome message
        setMessages([
          {
            id: 'welcome',
            text: `Session started with ${expertName}. You have 30 minutes.`,
            sender: 'expert',
            timestamp: new Date(),
            status: 'delivered',
          },
        ]);

      } catch (err: any) {
        console.error('Agora Chat initialization error:', err);
        setError(err.message || 'Failed to connect to chat');
        setLoading(false);
      }
    };

    initAgoraChat();

    // Cleanup on unmount
    return () => {
      if (connRef.current) {
        connRef.current.close();
      }
    };
  }, [bookingId, expertAgoraId, expertName, handleMessage]);

  // Session timer
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleSend = async () => {
    if (!inputText.trim() || !connRef.current || !isConnected) return;

    const messageId = Date.now().toString();
    const newMessage: Message = {
      id: messageId,
      text: inputText.trim(),
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
    };

    // Add message to UI immediately
    setMessages((prev) => [...prev, newMessage]);
    const messageText = inputText.trim();
    setInputText('');

    try {
      // Create and send Agora Chat message
      const msg = AC.message.create({
        type: 'txt',
        chatType: 'singleChat',
        to: expertAgoraId,
        msg: messageText,
      });

      await connRef.current.send(msg);

      // Update message status to sent
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status: 'sent' as const } : m))
      );
    } catch (err) {
      console.error('Error sending message:', err);
      // Update message status to failed
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status: 'failed' as const } : m))
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEndSession = () => {
    if (connRef.current) {
      connRef.current.close();
    }
    navigate(routes.rateExpert.replace(':bookingId', bookingId || ''));
  };

  // Error screen
  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center h-screen p-4"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <CText type="B18" color={colors.alertColor} style={{ marginBottom: 16 }}>
          Connection Error
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ textAlign: 'center', marginBottom: 24 }}>
          {error}
        </CText>
        <button
          onClick={() => navigate(routes.allBookings)}
          className="px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary }}
        >
          <CText type="M14" color={colors.white}>
            Back to Bookings
          </CText>
        </button>
      </div>
    );
  }

  // Loading screen
  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center h-screen"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: colors.primary }}
        >
          <CText type="B24" color={colors.white}>
            {expertName.charAt(0)}
          </CText>
        </div>
        <CText type="M16" color={colors.textColor} style={{ marginBottom: 8 }}>
          Connecting to {expertName}...
        </CText>
        <div className="flex gap-2">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary }}
          />
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary, animationDelay: '150ms' }}
          />
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary, animationDelay: '300ms' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {/* Header */}
      <div
        className="flex items-center p-4 border-b"
        style={{ borderColor: colors.borderColor }}
      >
        <button onClick={() => navigate(-1)} className="mr-3 p-2">
          {React.createElement(FiArrowLeft as any, { size: 20, color: colors.textColor })}
        </button>

        {/* Expert Info */}
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
          style={{ backgroundColor: colors.primary }}
        >
          <CText type="B14" color={colors.white}>
            {expertName.charAt(0)}
          </CText>
        </div>
        <div className="flex-1">
          <CText type="M14" color={colors.textColor}>
            {expertName}
          </CText>
          <div className="flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: isExpertOnline || isConnected ? colors.green : colors.grayScale500 }}
            />
            <CText type="r12" color={colors.grayScale500}>
              {isConnected ? (isExpertOnline ? 'Online' : 'In session') : 'Connecting...'}
            </CText>
          </div>
        </div>

        {/* Session Timer */}
        <div
          className="px-3 py-1 rounded-full mr-2"
          style={{
            backgroundColor: sessionTime < 300 ? colors.alertColor + '20' : colors.primary + '20',
          }}
        >
          <CText
            type="M12"
            color={sessionTime < 300 ? colors.alertColor : colors.primary}
          >
            {formatTime(sessionTime)}
          </CText>
        </div>

        <button className="p-2">
          {React.createElement(FiMoreVertical as any, { size: 20, color: colors.textColor })}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex mb-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="max-w-[75%] rounded-2xl px-4 py-2"
              style={{
                backgroundColor: message.sender === 'user' ? colors.primary : colors.inputBackground,
                borderBottomRightRadius: message.sender === 'user' ? 4 : 16,
                borderBottomLeftRadius: message.sender === 'expert' ? 4 : 16,
                opacity: message.status === 'sending' ? 0.7 : 1,
              }}
            >
              <CText
                type="r14"
                color={message.sender === 'user' ? colors.white : colors.textColor}
              >
                {message.text}
              </CText>
              <div className="flex justify-end mt-1 gap-1 items-center">
                <CText
                  type="r10"
                  color={message.sender === 'user' ? colors.white + '80' : colors.grayScale500}
                >
                  {formatMessageTime(message.timestamp)}
                </CText>
                {message.sender === 'user' && (
                  <CText
                    type="r10"
                    color={message.status === 'failed' ? colors.alertColor : colors.white + '80'}
                  >
                    {message.status === 'sending' && '...'}
                    {message.status === 'sent' && '✓'}
                    {message.status === 'delivered' && '✓✓'}
                    {message.status === 'read' && '✓✓'}
                    {message.status === 'failed' && '!'}
                  </CText>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Session Time Warning */}
      {sessionTime === 0 && (
        <div
          className="p-3 text-center"
          style={{ backgroundColor: colors.alertColor + '20' }}
        >
          <CText type="M14" color={colors.alertColor}>
            Session ended. Thank you for using our service!
          </CText>
          <button
            className="mt-2 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary }}
            onClick={handleEndSession}
          >
            <CText type="M14" color={colors.white}>
              Rate Expert
            </CText>
          </button>
        </div>
      )}

      {/* Input Area */}
      {sessionTime > 0 && (
        <div
          className="p-4 border-t flex items-center gap-2"
          style={{ borderColor: colors.borderColor }}
        >
          <button className="p-2">
            {React.createElement(FiPaperclip as any, { size: 20, color: colors.grayScale500 })}
          </button>

          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-full outline-none"
            style={{
              backgroundColor: colors.inputBackground,
              color: colors.textColor,
            }}
            disabled={!isConnected}
          />

          <button
            onClick={handleSend}
            disabled={!inputText.trim() || !isConnected}
            className="p-3 rounded-full transition-all"
            style={{
              backgroundColor: inputText.trim() && isConnected ? colors.primary : colors.grayScale500,
            }}
          >
            {React.createElement(FiSend as any, { size: 18, color: colors.white })}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatScreen;
